/**
 * DrawerResizeHandle — presentational resize grip for left-handle drawers.
 *
 * Renders the pill affordance used by right-edge-anchored slide-in drawers
 * (TraceDrawer, HuntStatusDrawer). Visual classes are copied verbatim from
 * `Drawer.tsx` in this directory. Positioning (absolute, top/left/translate)
 * is intentionally omitted here — callers supply it via `className` so each
 * drawer can place the handle appropriately for its layout.
 */

import React from 'react';

import { cn } from '@/utils/classname';

interface DrawerResizeHandleProps {
  /** Attach to the parent drawer's usePanelResize.onResizeHandleMouseDown. */
  onMouseDown: (e: React.MouseEvent) => void;
  /**
   * Positioning override for the mount context — e.g.
   * `"absolute top-1/2 -left-10 -translate-y-1/2"`.
   * All non-positional visual classes are baked in.
   */
  className?: string;
}

export function DrawerResizeHandle({
  onMouseDown,
  className,
}: DrawerResizeHandleProps) {
  return (
    <div
      onMouseDown={onMouseDown}
      className={cn(
        'border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary flex h-20 w-6 cursor-ew-resize items-center justify-center rounded-full border-2 transition-colors',
        className
      )}
      role="separator"
      aria-label="Resize drawer"
      title="Resize drawer"
    >
      <div className="flex flex-row gap-1 p-2">
        <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
        <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
      </div>
    </div>
  );
}
